import {
  DestroyRef,
  computed,
  effect,
  inject,
  Injectable,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ClientSessionService } from '@brighthub/uisession';
import { CREDIT_OVERVIEW_API } from './credit-overview-api.token';
import type {
  CreditReportSummaryPayload,
  CreditSubscriptionStatus,
} from './credit-overview.model';
import {
  buildCreditTimeline,
  isInProgressStatus,
  toCreditOverviewUiState,
} from './credit-overview-timeline';
import { CreditStatusContractError } from './http/credit-overview-response.guard';

const POLL_INTERVAL_MS = 5000;
const POST_INVITE_POLL_LIMIT = 24;

@Injectable()
export class CreditOverviewStore {
  private readonly _api = inject(CREDIT_OVERVIEW_API);
  private readonly _session = inject(ClientSessionService);
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _status = signal<CreditSubscriptionStatus | null>(null);
  private readonly _activeGuid = signal<string | null>(null);
  private readonly _loading = signal(false);
  private readonly _error = signal<unknown | null>(null);
  private readonly _summary = signal<CreditReportSummaryPayload | null>(null);
  private readonly _summaryLoading = signal(false);
  private readonly _summaryError = signal<unknown | null>(null);
  private readonly _reportLoading = signal(false);
  private readonly _reportError = signal<unknown | null>(null);
  private _pollTimer: ReturnType<typeof setTimeout> | null = null;
  private _postInvitePollsRemaining = 0;
  private _requestVersion = 0;
  private _destroyed = false;

  readonly status = this._status.asReadonly();
  readonly activeGuid = this._activeGuid.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly error = this._error.asReadonly();
  readonly summary = this._summary.asReadonly();
  readonly summaryLoading = this._summaryLoading.asReadonly();
  readonly summaryError = this._summaryError.asReadonly();
  readonly reportLoading = this._reportLoading.asReadonly();
  readonly reportError = this._reportError.asReadonly();

  readonly sessionGuid = computed(
    () => this._session.selectedSubjectData()?.guid?.trim() || null,
  );
  readonly uiState = computed(() => {
    const status = this._status();
    return status ? toCreditOverviewUiState(status) : null;
  });
  readonly timeline = computed(() => {
    const status = this._status();
    return status ? buildCreditTimeline(status) : [];
  });

  constructor() {
    effect(() => {
      const guid = this.sessionGuid();
      this._stopPolling();
      if (guid) {
        void this.load(guid);
      } else {
        this._reset();
      }
    });
    this._destroyRef.onDestroy(() => {
      this._destroyed = true;
      this._stopPolling();
      this._reset();
    });
  }

  async load(guid: string): Promise<void> {
    await this._refresh(guid, false, ++this._requestVersion);
  }

  startPostInvitePolling(guid: string): void {
    if (this._destroyed || this._activeGuid() !== guid) return;
    this._requestVersion++;
    this._postInvitePollsRemaining = POST_INVITE_POLL_LIMIT;
    this._stopPolling();
    this._schedulePoll(guid);
  }

  private async _refresh(
    guid: string,
    background: boolean,
    requestVersion: number,
  ): Promise<void> {
    this._stopPolling();
    if (this._activeGuid() !== guid) {
      this._postInvitePollsRemaining = 0;
      this._status.set(null);
      this._clearSummary();
      this._clearReportState();
    }
    this._activeGuid.set(guid);
    if (!background) {
      this._loading.set(true);
      this._error.set(null);
    }
    try {
      const result = await firstValueFrom(this._api.getStatus(guid));
      if (!this._isCurrent(guid, requestVersion)) return;
      this._status.set(result.subscription.status);
      if (isInProgressStatus(result.subscription.status)) {
        this._postInvitePollsRemaining = 0;
        this._clearSummary();
        this._schedulePoll(guid);
      } else if (result.subscription.status === 'FULFILLED') {
        this._postInvitePollsRemaining = 0;
        await this._loadSummary(guid, requestVersion);
      } else {
        this._clearSummary();
        this._continuePostInvitePolling(guid);
      }
    } catch (error) {
      if (!this._isCurrent(guid, requestVersion)) return;
      if (background && !(error instanceof CreditStatusContractError)) {
        const status = this._status();
        if (status && isInProgressStatus(status)) {
          this._schedulePoll(guid);
        } else {
          this._continuePostInvitePolling(guid);
        }
      } else {
        this._postInvitePollsRemaining = 0;
        if (background) {
          this._status.set(null);
          this._clearSummary();
        }
        this._error.set(error);
      }
    } finally {
      if (!background && this._isCurrent(guid, requestVersion)) {
        this._loading.set(false);
      }
    }
  }

  async loadReport(): Promise<Blob | null> {
    const guid = this._activeGuid();
    if (!guid || this._status() !== 'FULFILLED' || this._reportLoading()) {
      return null;
    }
    const requestVersion = this._requestVersion;
    this._reportLoading.set(true);
    this._reportError.set(null);
    try {
      const report = await firstValueFrom(this._api.getReport(guid));
      return this._isCurrent(guid, requestVersion) ? report : null;
    } catch (error) {
      if (this._isCurrent(guid, requestVersion)) {
        this._reportError.set(error);
      }
      return null;
    } finally {
      if (this._isCurrent(guid, requestVersion)) {
        this._reportLoading.set(false);
      }
    }
  }

  private _schedulePoll(guid: string): void {
    this._pollTimer = setTimeout(() => {
      this._pollTimer = null;
      if (!this._destroyed && this._activeGuid() === guid) {
        void this._refresh(guid, true, ++this._requestVersion);
      }
    }, POLL_INTERVAL_MS);
  }

  private _continuePostInvitePolling(guid: string): void {
    if (this._postInvitePollsRemaining <= 0) return;
    this._postInvitePollsRemaining--;
    if (this._postInvitePollsRemaining > 0) this._schedulePoll(guid);
  }

  private _stopPolling(): void {
    if (this._pollTimer !== null) {
      clearTimeout(this._pollTimer);
      this._pollTimer = null;
    }
  }

  private _reset(): void {
    this._requestVersion++;
    this._postInvitePollsRemaining = 0;
    this._activeGuid.set(null);
    this._status.set(null);
    this._loading.set(false);
    this._error.set(null);
    this._clearSummary();
    this._clearReportState();
  }

  private async _loadSummary(
    guid: string,
    requestVersion: number,
  ): Promise<void> {
    this._summaryLoading.set(true);
    this._summaryError.set(null);
    try {
      const summary = await firstValueFrom(this._api.getReportSummary(guid));
      if (this._isCurrent(guid, requestVersion)) {
        this._summary.set(summary);
      }
    } catch (error) {
      if (this._isCurrent(guid, requestVersion)) {
        this._summaryError.set(error);
      }
    } finally {
      if (this._isCurrent(guid, requestVersion)) {
        this._summaryLoading.set(false);
      }
    }
  }

  private _clearSummary(): void {
    this._summary.set(null);
    this._summaryLoading.set(false);
    this._summaryError.set(null);
  }

  private _clearReportState(): void {
    this._reportLoading.set(false);
    this._reportError.set(null);
  }

  private _isCurrent(guid: string, requestVersion: number): boolean {
    return (
      !this._destroyed &&
      this._activeGuid() === guid &&
      this._requestVersion === requestVersion
    );
  }
}
